import { Backdrop, CircularProgress } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import OnboardingService from '../../services/OnboardingService'
import Database from '../../types/Database'

import CreateDeskStep from './CreateDeskStep'
import CreateOfficeStep from './CreateOfficeStep'
import DeskCreationPromptStep from './DeskCreationPromptStep'
import JoinOfficeInstructionsStep from './JoinOfficeInstructionsStep'
import OfficeChoiceStep from './OfficeChoiceStep'
import WizardStepContainer from './WizardStepContainer'

interface OnboardingWizardProps {
  user: Database.User
}

/**
 * OnboardingWizard - Main container component for the onboarding wizard
 *
 * Manages wizard state, step transitions, and navigation history.
 * Persists state to database via OnboardingService.
 *
 * Requirements: 2.5, 7.1, 7.2, 9.1, 9.2
 */
const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ user }) => {
  const navigate = useNavigate()

  // Current step state
  const [currentStep, setCurrentStep] =
    useState<Database.OnboardingStep>('office_choice')

  // Navigation history for back button
  const [navigationHistory, setNavigationHistory] = useState<
    Database.OnboardingStep[]
  >([])

  // Loading state
  const [isLoading, setIsLoading] = useState(true)

  // Store created office for desk creation flow
  const [createdOffice, setCreatedOffice] = useState<Database.Office | null>(
    null
  )

  // Use a ref to store the office immediately for synchronous access
  const createdOfficeRef = React.useRef<Database.Office | null>(null)

  // Total steps for progress calculation (excluding not_started and complete)
  const TOTAL_STEPS = 5

  /**
   * Load onboarding state from database on mount
   * Requirements: 7.2 - Resume from last completed step
   */
  useEffect(() => {
    const loadOnboardingState = async () => {
      if (!user.uid) return

      try {
        setIsLoading(true)
        let onboarding = await OnboardingService.getUserOnboarding(user.uid)

        // Create onboarding record if it doesn't exist
        if (!onboarding) {
          onboarding = await OnboardingService.createOnboardingRecord(user.uid)
        }

        // Resume from saved step if not complete
        if (
          !onboarding.isComplete &&
          onboarding.currentStep !== 'not_started'
        ) {
          setCurrentStep(onboarding.currentStep)
        }
      } catch (error) {
        console.error('Failed to load onboarding state:', error)
        // Continue with default state if loading fails
      } finally {
        setIsLoading(false)
      }
    }

    loadOnboardingState()
  }, [user.uid])

  /**
   * Save progress to database
   * Requirements: 7.1 - Store current wizard step in database
   */
  const saveProgress = useCallback(
    async (step?: Database.OnboardingStep) => {
      if (!user.uid) return

      try {
        await OnboardingService.updateOnboardingStep(
          user.uid,
          step || currentStep
        )
      } catch (error) {
        console.error('Failed to save onboarding progress:', error)
        // Don't block user if save fails
      }
    },
    [user.uid, currentStep]
  )

  /**
   * Exit the wizard and navigate to home
   * Requirements: 9.1, 9.2 - Exit wizard at any time
   *
   * Updated to mark onboarding as complete so user doesn't get redirected back
   */
  const exitWizard = useCallback(async () => {
    if (user.uid) {
      try {
        await OnboardingService.markOnboardingComplete(user.uid)
        console.log('Onboarding marked as complete on exit')
      } catch (error) {
        console.error('Failed to mark onboarding complete on exit:', error)
      }
    }
    navigate('/home')
  }, [navigate, user.uid])

  /**
   * Complete onboarding and navigate to home
   * Requirements: 9.3, 9.4, 9.5 - Mark onboarding complete and navigate to calendar
   */
  const completeOnboarding = useCallback(async () => {
    if (!user.uid) return

    try {
      await OnboardingService.markOnboardingComplete(user.uid)
      setCurrentStep('complete')

      // Navigate to home after a brief delay to show completion
      setTimeout(() => {
        navigate('/home')
      }, 1500)
    } catch (error) {
      console.error('Failed to complete onboarding:', error)
      // Navigate anyway to not block user
      navigate('/home')
    }
  }, [user.uid, navigate])

  /**
   * Navigate to a specific step
   * Requirements: 2.5 - Manage current step state
   */
  const goToStep = useCallback(
    async (step: Database.OnboardingStep) => {
      // Add current step to history before navigating
      setNavigationHistory(prev => [...prev, currentStep])
      setCurrentStep(step)

      // Persist step to database
      await saveProgress(step)
    },
    [currentStep, saveProgress]
  )

  /**
   * Navigate back to previous step
   * Requirements: 2.5 - Track navigation history for back button
   */
  const goBack = useCallback(() => {
    if (navigationHistory.length === 0) return

    const previousStep = navigationHistory[navigationHistory.length - 1]
    setNavigationHistory(prev => prev.slice(0, -1))
    setCurrentStep(previousStep)

    // Persist step to database
    saveProgress(previousStep)
  }, [navigationHistory, saveProgress])

  // Show loading state while fetching onboarding data
  if (isLoading) {
    return (
      <Backdrop
        sx={{
          color: theme => (theme.palette.mode === 'dark' ? '#fff' : '#000'),
          zIndex: theme => theme.zIndex.drawer + 1,
          backgroundColor: theme =>
            theme.palette.mode === 'dark'
              ? 'rgba(0, 0, 0, 0.95)'
              : 'rgba(255, 255, 255, 0.95)'
        }}
        open={true}
      >
        <CircularProgress color='inherit' size={60} />
      </Backdrop>
    )
  }

  // Render appropriate step component based on current step
  const renderStep = () => {
    switch (currentStep) {
      case 'office_choice':
        return (
          <OfficeChoiceStep
            onSelectJoin={() => goToStep('join_instructions')}
            onSelectCreate={() => goToStep('create_office')}
          />
        )

      case 'join_instructions':
        return (
          <JoinOfficeInstructionsStep onBack={goBack} onExit={exitWizard} />
        )

      case 'create_office':
        return (
          <CreateOfficeStep
            user={user}
            onSuccess={office => {
              console.log('Office created in wizard:', office)
              // Store in state, ref, AND sessionStorage for maximum reliability
              setCreatedOffice(office)
              createdOfficeRef.current = office
              sessionStorage.setItem(
                'onboarding_created_office',
                JSON.stringify(office)
              )
              // Force a small delay to ensure state is set
              setTimeout(() => {
                goToStep('desk_prompt')
              }, 50)
            }}
            onCancel={goBack}
          />
        )

      case 'desk_prompt':
        // Try multiple sources: ref, state, sessionStorage
        let officeForPrompt = createdOfficeRef.current || createdOffice
        if (!officeForPrompt || !officeForPrompt.oid) {
          const stored = sessionStorage.getItem('onboarding_created_office')
          if (stored) {
            try {
              officeForPrompt = JSON.parse(stored)
            } catch (e) {
              console.error('Failed to parse stored office:', e)
            }
          }
        }
        // Final fallback
        if (!officeForPrompt || !officeForPrompt.oid) {
          officeForPrompt = { name: 'Your Office', oid: '' } as Database.Office
        }
        console.log('Desk prompt - office:', officeForPrompt)
        return (
          <DeskCreationPromptStep
            office={officeForPrompt}
            onProceed={() => goToStep('create_desk')}
            onSkip={completeOnboarding}
          />
        )

      case 'create_desk':
        // Try multiple sources: ref, state, sessionStorage
        let officeForDesk = createdOfficeRef.current || createdOffice
        if (!officeForDesk || !officeForDesk.oid) {
          const stored = sessionStorage.getItem('onboarding_created_office')
          if (stored) {
            try {
              officeForDesk = JSON.parse(stored)
            } catch (e) {
              console.error('Failed to parse stored office:', e)
            }
          }
        }
        // Final fallback
        if (!officeForDesk || !officeForDesk.oid) {
          officeForDesk = { name: 'Your Office', oid: '' } as Database.Office
        }
        console.log('Create desk - office:', officeForDesk)
        return (
          <CreateDeskStep
            user={user}
            preSelectedOffice={officeForDesk}
            onSuccess={() => {
              // Clean up sessionStorage
              sessionStorage.removeItem('onboarding_created_office')
              completeOnboarding()
            }}
            onCancel={() => goToStep('desk_prompt')}
          />
        )

      case 'complete':
        return (
          <div>
            <h2>Onboarding Complete!</h2>
            <p>Redirecting to home...</p>
          </div>
        )

      default:
        return (
          <div>
            <h2>Unknown Step</h2>
            <button onClick={exitWizard}>Exit</button>
          </div>
        )
    }
  }

  return (
    <WizardStepContainer
      currentStep={currentStep}
      totalSteps={TOTAL_STEPS}
      onBack={navigationHistory.length > 0 ? goBack : undefined}
      onSkip={exitWizard}
      showBackButton={navigationHistory.length > 0}
      showSkipButton={currentStep !== 'complete'}
    >
      {renderStep()}
    </WizardStepContainer>
  )
}

export default OnboardingWizard
